import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { extractSearchKeyword, resolveInitialPage } from '@utils/sourceNavigation';

interface DocumentHighlightParams {
  highlightTerms: string[];
  initialPage: number;
  /** True when the URL named a page, so the viewer should not override it. */
  initialPageExplicit: boolean;
  /** Where to start looking for the highlight when the exact page is unknown. */
  preferredSearchPage?: number;
}

/** The page/highlight target a Documents URL names; shared with the workspace's source preview. */
export function documentHighlightParams(searchParams: URLSearchParams): DocumentHighlightParams {
  const highlightParam = searchParams.get('highlight');
  const pageParam = searchParams.get('page');
  const approxPageParam = searchParams.get('approxPage');
  let highlightTerms: string[] = [];
  if (highlightParam) {
    // #783: the param is normally an anchor buildSourceDocumentUrl already
    // extracted — re-running extraction can truncate it, and splitting on
    // whitespace turns a Latin phrase into stopwords highlighted document-wide.
    // Only legacy raw-question URLs (they contain whitespace) get extraction,
    // and the result is searched as one phrase.
    const looksLikeRawQuery = /\s/.test(highlightParam.trim());
    const extracted = looksLikeRawQuery ? extractSearchKeyword(highlightParam) : highlightParam;
    highlightTerms = extracted.trim() ? [extracted.trim()] : [];
  }
  const approxPage = approxPageParam ? Number.parseInt(approxPageParam, 10) : NaN;
  return {
    highlightTerms,
    initialPage: resolveInitialPage(pageParam),
    initialPageExplicit: pageParam !== null,
    preferredSearchPage: Number.isInteger(approxPage) && approxPage > 0 ? approxPage : undefined,
  };
}

/**
 * Derives the PDF highlight target from the URL. The params are the source of
 * truth: the page used to copy them into refs and delete them from the URL,
 * which lost the highlight on refresh, made the link unshareable, and left the
 * URL disagreeing with what was on screen.
 */
export function useDocumentHighlightParams(): DocumentHighlightParams {
  const [searchParams] = useSearchParams();
  const query = searchParams.toString();
  // Keyed on the query string so the terms array stays referentially stable across renders.
  return useMemo(() => documentHighlightParams(new URLSearchParams(query)), [query]);
}
